import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { GetZoneName } from '../Utils/utils';
import { channelLabel } from '../Utils/light_profiles';
import { buildLightCopy } from '../Utils/light_copy';

// Mounted afresh for each operation: cancel/back never writes a settings draft.
export default function LightCopyDialog({
  controller,
  currentZone,
  currentSubzone,
  catalog,
  cycles,
  baseline,
  disabled,
  onClose,
  onApply,
  validateCopy
}) {
  const { t } = useTranslation();
  const localScopeAvailable =
    Number.isInteger(currentZone) &&
    Number.isInteger(currentSubzone) &&
    Boolean(cycles[currentZone]) &&
    catalog.subzones.some((s) => s.zone === currentZone && s.subzone === currentSubzone);
  const localIdentity = () => ({ zone: currentZone, subzone: currentSubzone });
  const [request, setRequest] = useState(() =>
    localScopeAvailable
      ? { kind: 'local_profile', source: localIdentity(), target: localIdentity() }
      : { kind: '', source: {}, target: {} }
  );
  const [step, setStep] = useState(0);
  const [applyError, setApplyError] = useState('');
  const localProfile = request.kind === 'local_profile';
  const normalizedRequest = localProfile ? { ...request, kind: 'profile' } : request;
  const kind = normalizedRequest.kind;
  const fields = localProfile
    ? ['channel']
    : !request.kind || request.kind === 'zone'
      ? ['zone']
      : request.kind === 'subzone'
        ? ['zone', 'subzone']
        : ['zone', 'subzone', 'channel'];
  const steps = [
    { key: 'kind' },
    ...['source', 'target'].flatMap((side) =>
      fields.map((field) => ({ side, field, key: `${side}_${field}` }))
    ),
    { key: 'review' }
  ];
  const current = steps[step];
  const reviewing = current.key === 'review';
  const zoneName = (zone) => {
    const name = GetZoneName(controller, zone);
    return name && name !== 'unknown' ? name : `${t('lighting_zone')} ${Number(zone) + 1}`;
  };
  const zoneOptions = Object.values(controller.zones ?? {})
    .map((zone) => ({ zone, id: Number(zone.zone_id ?? zone.id) }))
    .filter(({ id }) => Number.isInteger(id) && id >= 0 && id <= 8)
    .sort((a, b) => a.id - b.id);
  let options = [];
  if (step === 0)
    options = ['zone', 'subzone', 'profile', 'local_profile'].map((value) => ({
      value,
      label:
        value === 'local_profile'
          ? t('lighting_copy_kind_local_profile', {
              defaultValue: 'One profile within this subzone'
            })
          : t(`lighting_copy_kind_${value}`),
      disabled: value === 'local_profile' && !localScopeAvailable
    }));
  else if (!reviewing) {
    const selected = request[current.side];
    if (current.field === 'zone')
      options = zoneOptions.map(({ id }) => ({
        value: id,
        label: zoneName(id),
        disabled:
          current.side === 'target' &&
          (!cycles[id] || (request.kind === 'zone' && id === request.source.zone))
      }));
    if (current.field === 'subzone')
      options = catalog.subzones
        .filter((s) => s.zone === selected.zone)
        .sort((a, b) => a.subzone - b.subzone)
        .map((s) => ({
          value: s.subzone,
          label: `${t('lighting_subzone')} ${s.subzone + 1}`,
          disabled:
            current.side === 'target' &&
            request.kind === 'subzone' &&
            selected.zone === request.source.zone &&
            s.subzone === request.source.subzone
        }));
    if (current.field === 'channel')
      options = catalog.channels
        .filter((c) => c.zone === selected.zone && c.subzone === selected.subzone)
        .sort((a, b) => a.channel - b.channel)
        .map((c) => ({
          value: c.channel,
          label: `${c.channel + 1} - ${channelLabel(c)}`,
          disabled:
            current.side === 'target' &&
            selected.zone === request.source.zone &&
            selected.subzone === request.source.subzone &&
            c.channel === request.source.channel
        }));
  }
  const value =
    step === 0 ? request.kind : reviewing ? '' : request[current.side][current.field] ?? '';
  const update = (value) => {
    setApplyError('');
    if (step === 0) {
      const zone = zoneOptions.some((option) => option.id === Number(currentZone))
        ? Number(currentZone)
        : undefined;
      setRequest({
        kind: value,
        source: value === 'local_profile' ? localIdentity() : zone === undefined ? {} : { zone },
        target:
          value === 'local_profile'
            ? localIdentity()
            : value !== 'zone' && cycles[zone]
              ? { zone }
              : {}
      });
    } else {
      const selected = {
        ...(localProfile ? localIdentity() : {}),
        ...Object.fromEntries(
          fields
            .slice(0, fields.indexOf(current.field))
            .map((field) => [field, request[current.side][field]])
        ),
        [current.field]: Number(value)
      };
      if (
        current.side === 'target' &&
        current.field === 'zone' &&
        kind === 'profile' &&
        selected.zone === request.source.zone
      ) {
        selected.subzone = request.source.subzone;
      }
      // A new source invalidates the old destination. Only its containing scope
      // is defaulted; the destination subzone/profile itself is always explicit.
      const target =
        current.side === 'source'
          ? {
              ...(request.kind !== 'zone' && cycles[selected.zone] ? { zone: selected.zone } : {}),
              ...(kind === 'profile' && selected.subzone !== undefined && cycles[selected.zone]
                ? { subzone: selected.subzone }
                : {})
            }
          : selected;
      setRequest({ ...request, [current.side]: selected, target });
    }
    setStep(step + 1);
  };
  const result = reviewing ? buildLightCopy(catalog, normalizedRequest, cycles, baseline) : null;
  const errors = reviewing ? [...result.errors, ...(validateCopy?.(normalizedRequest) ?? [])] : [];
  const label = (identity) =>
    [
      ...(identity.zone !== undefined ? [zoneName(identity.zone)] : []),
      ...(request.kind !== 'zone' && identity.subzone !== undefined
        ? [`${t('lighting_subzone')} ${identity.subzone + 1}`]
        : []),
      ...(kind === 'profile' && identity.channel !== undefined
        ? [
            channelLabel(
              catalog.channels.find(
                (c) =>
                  c.zone === identity.zone &&
                  c.subzone === identity.subzone &&
                  c.channel === identity.channel
              ) ?? {}
            )
          ]
        : [])
    ].join(' / ');
  const inScope = (channel, identity) =>
    channel.zone === identity.zone &&
    (kind === 'zone' ||
      (channel.subzone === identity.subzone &&
        (kind === 'profile'
          ? channel.channel === identity.channel
          : !(
              channel.subzone === 0 &&
              channel.channel === 0 &&
              catalog.zones.find((entry) => entry.zone === channel.zone)?.shared_toplight
            ))));
  const replaced = reviewing ? catalog.channels.filter((c) => inScope(c, request.target)) : [];
  const incoming = reviewing ? catalog.channels.filter((c) => inScope(c, request.source)) : [];
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} aria-labelledby="lighting-copy-title">
      <DialogTitle id="lighting-copy-title">{t('lighting_copy_title')}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary">
          {t('lighting_copy_step', { step: step + 1, count: steps.length })}
        </Typography>
        {reviewing ? (
          <>
            <Typography variant="h6" gutterBottom>
              {t('lighting_copy_review')}
            </Typography>
            <DialogContentText style={{ whiteSpace: 'pre-line' }}>
              {t(`lighting_copy_scope_${kind}`)}
            </DialogContentText>
            <Typography gutterBottom>
              {t('lighting_copy_source')}: {label(request.source)}
            </Typography>
            <Typography gutterBottom>
              {t('lighting_copy_target')}: {label(request.target)}
            </Typography>
            <DialogContentText>
              {t('lighting_copy_replace_count', {
                count: replaced.length,
                incoming: incoming.length
              })}
            </DialogContentText>
            {replaced.length > 0 && (
              <Box component="ul" style={{ maxHeight: 160, overflowY: 'auto' }}>
                {replaced.map((c) => (
                  <li key={`${c.subzone}:${c.channel}`}>
                    {t('lighting_subzone')} {c.subzone + 1} / {channelLabel(c)}
                  </li>
                ))}
              </Box>
            )}
            <DialogContentText>{t('lighting_copy_preserves')}</DialogContentText>
            {errors.map((error, index) => (
              <Alert key={index} severity="error">
                {error}
              </Alert>
            ))}
            {applyError && <Alert severity="error">{applyError}</Alert>}
          </>
        ) : (
          <Box py={2}>
            <TextField
              key={current.key}
              id="lighting-copy-choice"
              autoFocus
              fullWidth
              select
              label={t(`lighting_copy_${current.key}`)}
              value={value}
              disabled={disabled}
              onChange={(event) => update(event.target.value)}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  onClick={() => {
                    if (option.value === value && !disabled && !option.disabled)
                      update(option.value);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {step > 0 && (
              <Typography variant="body2" style={{ marginTop: 16 }}>
                {current.side === 'target'
                  ? `${t('lighting_copy_source')}: ${label(request.source)}`
                  : label(request.source) || t(`lighting_copy_kind_${request.kind}`)}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ flexWrap: 'wrap', gap: 8 }}>
        <Button onClick={onClose}>{t('lighting_cancel')}</Button>
        {step > 0 && (
          <Button
            onClick={() => {
              setApplyError('');
              setStep(step - 1);
            }}
          >
            {t('lighting_copy_back')}
          </Button>
        )}
        {reviewing ? (
          <Button
            color="primary"
            variant="contained"
            disabled={disabled || errors.length > 0}
            onClick={() => {
              // The parent revalidates against current settings before changing a draft.
              if (onApply(normalizedRequest)) onClose();
              else setApplyError(t('lighting_copy_failed'));
            }}
          >
            {t('lighting_copy_confirm')}
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => setStep(step + 1)}
            disabled={
              disabled ||
              value === '' ||
              !options.some((option) => option.value === value && !option.disabled)
            }
          >
            {t('lighting_copy_next')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
